<template>
  <div class="dcc-fulfillment-table">
    <title-panel :display-button="false" :no-counter="true">
      {{ t('dccFulfillmentTable.title') }}
    </title-panel>
    <p class="dcc-fulfillment-table__hint">
      {{ t('dccFulfillmentTable.hint') }}
    </p>

    <loading-wrapper :is-loading="isLoading" positioning="flex-center">
      <tree-table
        :data="treeData"
        :columns="columns"
        :empty-message="t('dccFulfillmentTable.emptyMessage')"
      />
    </loading-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from 'axios';
  import { useToast } from '@/composables/useToast';
  import endpointsList from '@/helpers/endpointsList';
  import TreeTable from '@/components/TreeTable/TreeTable.vue';
  import LoadingWrapper from '@/components/LoadingWrapper/LoadingWrapper.vue';
  import { TreeNode } from 'primevue/treenode';
  import { IDccFulfillment } from '@/interfaces/dcc-fulfillment';
  import TitlePanel from '@/components/TitlePanel/TitlePanel.vue';

  const props = defineProps<{
    workspaceId: string;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const isLoading = ref(true);
  const treeData = ref<TreeNode[]>([]);

  const columns = [
    {
      field: 'label',
      header: t('dccFulfillmentTable.columns.kks'),
      expander: true,
      width: '80%',
    },
    {
      field: 'fulfillment',
      header: t('dccFulfillmentTable.columns.fulfillment'),
      width: '20%',
    },
  ];

  function buildTree(data: IDccFulfillment): TreeNode[] {
    return Object.entries(data).map(([kks, groupList], kksIdx) => {
      let kksCurrent = 0;
      const kksTarget = groupList.length;

      const groupChildren: TreeNode[] = groupList.flatMap(
        (groupObj, groupIdx) =>
          Object.entries(groupObj).map(([groupDcc, dccList], dccGroupIdx) => {
            let groupCurrent = 0;
            const groupTarget = 1;

            const dccChildren: TreeNode[] = dccList.map((dccObj, dccIdx) => {
              const [dcc, docs] = Object.entries(dccObj)[0];
              const current = docs.length;
              const target = 1;
              groupCurrent += current;

              return {
                key: `${kksIdx}-${groupIdx}-${dccGroupIdx}-${dccIdx}`,
                data: {
                  label: dcc,
                  fulfillment: {
                    current,
                    target,
                  },
                },
              };
            });

            kksCurrent += groupCurrent;

            return {
              key: `${kksIdx}-${groupIdx}-${dccGroupIdx}`,
              data: {
                label: groupDcc,
                fulfillment: {
                  current: groupCurrent,
                  target: groupTarget,
                },
              },
              children: dccChildren,
            };
          }),
      );

      return {
        key: `${kksIdx}`,
        data: {
          label: kks,
          fulfillment: {
            current: kksCurrent,
            target: kksTarget,
          },
        },
        children: groupChildren,
      };
    });
  }

  async function loadFulfillment(): Promise<void> {
    isLoading.value = true;
    try {
      const response = await axios.get<IDccFulfillment>(
        endpointsList.workspaces.getDccFulfillment(props.workspaceId),
      );
      treeData.value = buildTree(response.data);
    } catch {
      createErrorToast(t('dccFulfillmentTable.errors.cannotLoadFulfillment'));
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(async () => {
    await loadFulfillment();
  });
</script>

<style scoped lang="scss">
  .dcc-fulfillment-table {
    &__hint {
      font-size: 16px;
      line-height: 28px;
      font-weight: 500;
    }
  }
  .title-panel {
    margin-top: $tab-view-margin-top;
  }
</style>
